import {
  Checkbox,
  CloseButton,
  Divider,
  Group,
  Input,
  Pill,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconPlus, IconSearch } from "@tabler/icons-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import "./MultiSelectCustom.css";

export interface MultiSelectCustomOption {
  value: string;
  label: string;
}

interface MultiSelectCustomProps {
  /** Danh sách lựa chọn */
  data: MultiSelectCustomOption[];
  /** Mảng các value đang được chọn */
  value: string[];
  /** Callback khi thay đổi lựa chọn */
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  /** Cho phép tìm kiếm trong dropdown */
  searchable?: boolean;
  /**
   * Giá trị ô tìm kiếm (controlled).
   * Truyền kèm `onSearchChange` để tự xử lý tìm kiếm (vd: gọi API phía server).
   * Bỏ trống thì component tự quản lý state và lọc phía client.
   */
  searchValue?: string;
  /**
   * Callback khi gõ vào ô tìm kiếm.
   * Nếu truyền vào, component sẽ KHÔNG lọc phía client nữa
   * (giả định `data` đã được lọc sẵn từ server).
   */
  onSearchChange?: (query: string) => void;
  /**
   * Độ trễ (ms) trước khi gọi `onSearchChange`.
   * Ô nhập luôn cập nhật tức thì, chỉ hoãn việc báo lên component cha
   * để tránh cha re-render theo từng phím. Đặt 0 để tắt debounce.
   */
  searchDebounce?: number;
  /**
   * Cho phép nhập nhiều giá trị cùng lúc, ngăn cách bởi `separator`.
   * Nhấn Enter hoặc bấm nút "Thêm N giá trị" để thêm tất cả vào lựa chọn.
   */
  allowMultipleInput?: boolean;
  /** Ký tự ngăn cách khi `allowMultipleInput` = true */
  separator?: string;
  /** Cho phép xóa toàn bộ lựa chọn */
  clearable?: boolean;
  /** Số pill hiển thị tối đa trước khi gộp thành "+N" (tránh tràn UI) */
  maxDisplayedValues?: number;
  size?: string;
  disabled?: boolean;
  /** Chiều cao tối đa của vùng danh sách (px) */
  maxDropdownHeight?: number;
  nothingFoundMessage?: string;
  /** Ẩn dòng "Chọn tất cả" */
  hideSelectAll?: boolean;
  /**
   * Hiển thị danh sách đang chọn ngay trong dropdown.
   * Hữu ích khi `data` đổi theo từ khóa/API, mã đã chọn có thể không còn trong danh sách.
   */
  showSelectedInDropdown?: boolean;
  /** Chiều cao tối đa của vùng "Đã chọn" trong dropdown (px) */
  maxSelectedHeight?: number;
  /** Icon/nội dung hiển thị bên trái control */
  leftSection?: ReactNode;
  /** Độ rộng vùng icon bên trái */
  leftSectionWidth?: number | string;
  /**
   * Icon/nội dung tùy chỉnh bên phải control.
   * Nếu truyền vào sẽ GHI ĐÈ nút xóa/mũi tên mặc định (giống Mantine).
   */
  rightSection?: ReactNode;
  /** Độ rộng vùng icon bên phải */
  rightSectionWidth?: number | string;
}

const MultiSelectCustom = ({
  data,
  value,
  onChange,
  label,
  placeholder = "Chọn...",
  searchable = true,
  searchValue,
  onSearchChange,
  searchDebounce = 300,
  allowMultipleInput = false,
  separator = ",",
  clearable = true,
  maxDisplayedValues = 2,
  size = "sm",
  disabled = false,
  maxDropdownHeight = 260,
  nothingFoundMessage = "Không tìm thấy kết quả",
  hideSelectAll = false,
  showSelectedInDropdown = true,
  maxSelectedHeight = 96,
  leftSection,
  leftSectionWidth,
  rightSection,
  rightSectionWidth,
}: MultiSelectCustomProps) => {
  const [opened, setOpened] = useState(false);

  // Ô tìm kiếm luôn chạy trên state nội bộ để gõ không bị khựng,
  // `onSearchChange` mới là thứ được debounce báo ngược lên cha.
  const [searchText, setSearchText] = useState(searchValue ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Giá trị gần nhất đã báo lên cha, dùng để phân biệt cha tự đổi searchValue
  const lastEmittedRef = useRef(searchValue ?? "");

  // Chỉ đồng bộ ngược khi cha CHỦ ĐỘNG đổi searchValue (vd: reset bộ lọc),
  // bỏ qua lần cha set lại đúng giá trị mình vừa phát ra.
  useEffect(() => {
    if (searchValue === undefined || searchValue === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = searchValue;
    setSearchText(searchValue);
  }, [searchValue]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const emitSearchChange = (query: string) => {
    lastEmittedRef.current = query;
    onSearchChange?.(query);
  };

  const handleSearchTextChange = (query: string) => {
    setSearchText(query);
    if (!onSearchChange) return;

    clearTimeout(debounceRef.current);
    if (searchDebounce > 0) {
      debounceRef.current = setTimeout(
        () => emitSearchChange(query),
        searchDebounce,
      );
    } else {
      emitSearchChange(query);
    }
  };

  // Xóa ô tìm kiếm ngay lập tức, không chờ debounce
  const resetSearchText = () => {
    clearTimeout(debounceRef.current);
    setSearchText("");
    if (onSearchChange) emitSearchChange("");
  };

  // Các option đang được chọn. Mã không có trong `data`
  // (do server chỉ trả về theo từ khóa, hoặc do nhập tay) vẫn hiển thị bằng chính value.
  const selectedOptions = useMemo(
    () =>
      value.map(
        (v) => data.find((item) => item.value === v) ?? { value: v, label: v },
      ),
    [data, value],
  );

  // Lọc theo từ khóa tìm kiếm (bỏ qua nếu đã lọc phía server)
  const filteredData = useMemo(() => {
    if (onSearchChange) return data;
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return data;
    return data.filter((item) => item.label.toLowerCase().includes(keyword));
  }, [data, searchText, onSearchChange]);

  // Tách từ khóa thành nhiều giá trị theo separator, bỏ trùng và bỏ mã đã chọn
  const pendingValues = useMemo(() => {
    if (!allowMultipleInput) return [];
    const tokens = searchText
      .split(separator)
      .map((token) => token.trim())
      .filter(Boolean);
    return Array.from(new Set(tokens)).filter(
      (token) => !value.includes(token),
    );
  }, [allowMultipleInput, searchText, separator, value]);

  // Chỉ hiện nút "Thêm" khi thực sự nhập nhiều giá trị
  const canAddPendingValues =
    pendingValues.length > 0 && searchText.includes(separator);

  const allChecked =
    data.length > 0 && data.every((item) => value.includes(item.value));
  const indeterminate = value.length > 0 && !allChecked;

  const toggleItem = (itemValue: string) => {
    if (value.includes(itemValue)) {
      onChange(value.filter((v) => v !== itemValue));
    } else {
      onChange([...value, itemValue]);
    }
  };

  const handleToggleAll = () => {
    const dataValues = data.map((item) => item.value);
    // Chỉ thao tác trên các option đang hiển thị, giữ nguyên lựa chọn ngoài danh sách
    if (allChecked) onChange(value.filter((v) => !dataValues.includes(v)));
    else onChange(Array.from(new Set([...value, ...dataValues])));
  };

  const handleAddPendingValues = () => {
    if (pendingValues.length === 0) return;
    onChange([...value, ...pendingValues]);
    resetSearchText();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  // Chỉ hiển thị tối đa maxDisplayedValues pill, phần còn lại gộp thành "+N"
  const visibleOptions = selectedOptions.slice(0, maxDisplayedValues);
  const restCount = selectedOptions.length - visibleOptions.length;
  const hasValue = value.length > 0;

  return (
    <Input.Wrapper label={label} size={size}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width="target"
        shadow="md"
        withinPortal
        disabled={disabled}
      >
        <Popover.Target>
          <Input
            component="div"
            pointer
            size={size}
            disabled={disabled}
            onClick={() => !disabled && setOpened((o) => !o)}
            leftSection={leftSection}
            leftSectionWidth={leftSectionWidth}
            rightSectionWidth={rightSectionWidth}
            rightSectionPointerEvents={
              rightSection ? "none" : hasValue && clearable ? "all" : "none"
            }
            rightSection={
              // Ưu tiên rightSection tùy chỉnh, nếu không có thì dùng nút xóa/mũi tên mặc định
              rightSection ??
              (clearable && hasValue ? (
                <CloseButton
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                  aria-label="Xóa lựa chọn"
                />
              ) : (
                <IconChevronDown
                  size={16}
                  style={{
                    transform: opened ? "rotate(180deg)" : undefined,
                    transition: "transform 150ms ease",
                  }}
                />
              ))
            }
          >
            {hasValue ? (
              <div className="multiselect-custom-values">
                {visibleOptions.map((opt) => (
                  // Click vào pill vẫn mở/đóng dropdown như click vùng trống.
                  // Riêng nút xóa đã được Mantine stopPropagation sẵn nên không bị lọt.
                  <Pill
                    key={opt.value}
                    withRemoveButton
                    disabled={disabled}
                    onRemove={() =>
                      onChange(value.filter((v) => v !== opt.value))
                    }
                  >
                    {opt.label}
                  </Pill>
                ))}
                {restCount > 0 && (
                  <Pill style={{ flexShrink: 0 }}>+{restCount}</Pill>
                )}
              </div>
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </Input>
        </Popover.Target>

        <Popover.Dropdown p={4}>
          {searchable && (
            <TextInput
              placeholder={
                allowMultipleInput
                  ? `Tìm kiếm, nhập nhiều giá trị cách nhau bởi "${separator}"`
                  : "Tìm kiếm..."
              }
              value={searchText}
              onChange={(e) => handleSearchTextChange(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAddPendingValues) {
                  e.preventDefault();
                  handleAddPendingValues();
                }
              }}
              leftSection={<IconSearch size={14} />}
              size="xs"
              mb={4}
            />
          )}

          {canAddPendingValues && (
            <>
              <UnstyledButton
                className="multiselect-custom-option"
                onClick={handleAddPendingValues}
              >
                <IconPlus size={14} />
                <Text size="sm" style={{ lineHeight: 1.2 }}>
                  Thêm {pendingValues.length} giá trị đã nhập
                </Text>
              </UnstyledButton>
              <Divider my={2} />
            </>
          )}

          {showSelectedInDropdown && hasValue && (
            <>
              <Group justify="space-between" px={8} py={4} wrap="nowrap">
                <Text size="xs" fw={500}>
                  Đã chọn ({selectedOptions.length})
                </Text>
                <UnstyledButton onClick={() => onChange([])}>
                  <Text size="xs" c="red">
                    Bỏ chọn tất cả
                  </Text>
                </UnstyledButton>
              </Group>
              <ScrollArea.Autosize mah={maxSelectedHeight} type="scroll">
                <Group gap={4} px={8} pb={6}>
                  {selectedOptions.map((opt) => (
                    <Pill
                      key={opt.value}
                      withRemoveButton
                      disabled={disabled}
                      onRemove={() =>
                        onChange(value.filter((v) => v !== opt.value))
                      }
                    >
                      {opt.label}
                    </Pill>
                  ))}
                </Group>
              </ScrollArea.Autosize>
              <Divider mb={2} />
            </>
          )}

          {!hideSelectAll && data.length > 0 && (
            <>
              <Group justify="space-between" px={8} py={4} wrap="nowrap">
                <Checkbox
                  label="Chọn tất cả"
                  size="xs"
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={handleToggleAll}
                />
                <Text size="xs" c="dimmed">
                  {data.filter((item) => value.includes(item.value)).length}/
                  {data.length}
                </Text>
              </Group>
              <Divider mb={2} />
            </>
          )}

          <ScrollArea.Autosize mah={maxDropdownHeight} type="scroll">
            {filteredData.length === 0 ? (
              <Text size="xs" c="dimmed" ta="center" py="sm">
                {nothingFoundMessage}
              </Text>
            ) : (
              filteredData.map((item) => {
                const checked = value.includes(item.value);
                return (
                  <UnstyledButton
                    key={item.value}
                    className="multiselect-custom-option"
                    data-selected={checked || undefined}
                    onClick={() => toggleItem(item.value)}
                  >
                    <Checkbox
                      checked={checked}
                      readOnly
                      size="xs"
                      tabIndex={-1}
                      style={{ pointerEvents: "none" }}
                    />
                    <Text size="sm" style={{ lineHeight: 1.2 }}>
                      {item.label}
                    </Text>
                  </UnstyledButton>
                );
              })
            )}
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    </Input.Wrapper>
  );
};

export default MultiSelectCustom;
